import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Alert, Button, FormControlLabel, MenuItem, Stack, Switch, TextField, Typography } from "@mui/material";
import RouterRoundedIcon from "@mui/icons-material/RouterRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import {
  useGetProvidersQuery, useCreateProviderMutation, useUpdateProviderMutation, useDeleteProviderMutation, useGetCompaniesQuery,
} from "../../../Api/platformApi";
import {
  AppPage, PageHeader, FilterBar, SearchBox, DataTable, StatusBadge,
  ActionButtons, ActionButton, SlidePanel, ConfirmDialog, type DataTableColumn,
} from "../../../Components/ui";

const API_URL = process.env.REACT_APP_API_URL;

const empty = {
  company_id: "" as number | "",
  provider_type: "sms", provider_name: "telnyx", active: true,
  api_key: "", api_secret: "", messaging_profile_id: "",
};

/**
 * Provider Settings — multi-tenant aware.
 *
 * Super admins must pick the owning company before adding a provider (and can
 * filter the list by company). Company admins are locked to their own company:
 * the dropdown is hidden and their company_id is used automatically.
 */
const Providers: React.FC = () => {
  const { data, isLoading } = useGetProvidersQuery();
  const [createProvider] = useCreateProviderMutation();
  const [updateProvider] = useUpdateProviderMutation();
  const [deleteProvider] = useDeleteProviderMutation();

  // Who am I? Drives whether the company picker shows and which company is used.
  const [isSuper, setIsSuper] = useState(false);
  const [myCompanyId, setMyCompanyId] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    axios.post(`${API_URL}/user/userInfo`, {}, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
    }).then((res) => {
      if (cancelled) return;
      setIsSuper(Boolean(res.data?.data?.is_super_admin));
      setMyCompanyId(res.data?.data?.current_company_id ?? null);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const { data: companiesData } = useGetCompaniesQuery(undefined, { skip: !isSuper });
  const companies = companiesData?.data ?? [];

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<any>(empty);
  const [search, setSearch] = useState("");
  const [companyFilter, setCompanyFilter] = useState<number | "">("");
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [confirmId, setConfirmId] = useState<number | null>(null);

  const rows = data?.data ?? [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((p: any) => {
      if (companyFilter !== "" && p.company_id !== companyFilter) return false;
      if (!q) return true;
      return [p.provider_type, p.provider_name, p.company_name]
        .filter(Boolean).some((v: string) => v.toLowerCase().includes(q));
    });
  }, [rows, search, companyFilter]);

  const set = (k: string) => (e: any) => setForm({ ...form, [k]: e.target.value });

  const openCreate = () => { setEditingId(null); setForm(empty); setError(null); setOpen(true); };

  // Edit: prefill the non-secret fields. Credentials are never returned by the
  // API, so the key/secret fields stay blank and are only overwritten if typed.
  const openEdit = (p: any) => {
    setEditingId(p.id);
    setForm({
      company_id: p.company_id ?? "",
      provider_type: p.provider_type ?? "sms",
      provider_name: p.provider_name ?? "telnyx",
      active: Boolean(p.active),
      api_key: "", api_secret: "", messaging_profile_id: "",
    });
    setError(null);
    setOpen(true);
  };

  const save = async () => {
    setError(null);
    // Super admin must choose the owning company; company admins use their own.
    const companyId = isSuper ? (form.company_id || null) : myCompanyId;
    if (isSuper && !companyId) {
      setError("Please select a company before adding a provider.");
      return;
    }

    const credentials: any = {};
    if (form.api_key) credentials.api_key = form.api_key;
    if (form.api_secret) credentials.api_secret = form.api_secret;
    if (form.messaging_profile_id) credentials.messaging_profile_id = form.messaging_profile_id;

    try {
      if (editingId) {
        const body: any = {
          provider_type: form.provider_type,
          provider_name: form.provider_name,
          active: form.active,
        };
        // Only send credentials when the user entered new values — otherwise
        // omit the key so the backend keeps the existing (encrypted) secrets.
        if (Object.keys(credentials).length > 0) body.credentials = credentials;
        await updateProvider({ id: editingId, companyId: companyId ?? undefined, body }).unwrap();
      } else {
        await createProvider({
          companyId: companyId ?? undefined,
          body: {
            provider_type: form.provider_type,
            provider_name: form.provider_name,
            active: form.active,
            credentials,
          },
        }).unwrap();
      }
      setOpen(false);
      setEditingId(null);
      setForm(empty);
    } catch (e: any) {
      setError(e?.data?.message || "Failed to save provider.");
    }
  };

  const columns: DataTableColumn<any>[] = [
    { id: "provider_name", label: "Provider", render: (p) => p.provider_name },
    ...(isSuper ? [{
      id: "company_name", label: "Company",
      render: (p: any) => p.company_name || "—",
    } as DataTableColumn<any>] : []),
    {
      id: "provider_type", label: "Type",
      render: (p) => <StatusBadge status={p.provider_type} tone="info" />,
    },
    {
      id: "has_credentials", label: "Credentials",
      render: (p) => <StatusBadge status={p.has_credentials ? "configured" : "none"} />,
    },
    {
      id: "active", label: "Status",
      render: (p) => <StatusBadge status={p.active ? "active" : "inactive"} />,
    },
  ];

  return (
    <AppPage
      disablePaper
      header={
        <PageHeader
          icon={<RouterRoundedIcon />}
          title="Provider Settings"
          subtitle="SMS and voice carrier integrations"
          actions={
            <ActionButton startIcon={<AddRoundedIcon />} onClick={openCreate}>
              Add Provider
            </ActionButton>
          }
        />
      }
    >
      <Alert severity="info" sx={{ mb: 2 }}>
        Credentials are encrypted at rest and never displayed again.
      </Alert>

      <FilterBar right={<Typography fontSize={13} color="text.secondary">{filtered.length} providers</Typography>}>
        <SearchBox value={search} onChange={setSearch} placeholder="Search providers…" />
        {isSuper && (
          <TextField
            select size="small" label="Company"
            value={companyFilter}
            onChange={(e) => setCompanyFilter(e.target.value === "" ? "" : Number(e.target.value))}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">All Companies</MenuItem>
            {companies.map((c: any) => (
              <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
            ))}
          </TextField>
        )}
      </FilterBar>

      <DataTable
        columns={columns}
        rows={filtered}
        loading={isLoading}
        actions={(p) => (
          <ActionButtons
            onEdit={() => openEdit(p)}
            onDelete={() => setConfirmId(p.id)}
          />
        )}
        empty={{
          icon: <RouterRoundedIcon />,
          title: "No providers yet",
          message: "Add your first SMS or voice carrier to enable messaging.",
          action: <ActionButton startIcon={<AddRoundedIcon />} onClick={openCreate}>Add Provider</ActionButton>,
        }}
      />

      <SlidePanel
        open={open}
        onClose={() => setOpen(false)}
        title={editingId ? "Edit Provider" : "Add Provider"}
        subtitle={editingId ? "Update this carrier integration" : "Connect a carrier integration"}
        icon={<RouterRoundedIcon />}
        footer={
          <>
            <Button variant="outlined" onClick={() => setOpen(false)} sx={{ borderRadius: "10px" }}>Cancel</Button>
            <ActionButton onClick={save} disabled={!form.provider_name || !form.provider_type}>
              {editingId ? "Save Changes" : "Save Provider"}
            </ActionButton>
          </>
        }
      >
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          {isSuper && (
            <TextField
              select required label="Company"
              value={form.company_id}
              onChange={set("company_id")}
              fullWidth
              disabled={Boolean(editingId)}
              helperText={editingId ? "Company can't be changed after creation" : "The company this provider belongs to"}
            >
              {companies.map((c: any) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </TextField>
          )}
          <TextField select label="Type" value={form.provider_type} onChange={set("provider_type")} fullWidth>
            <MenuItem value="sms">SMS</MenuItem>
            <MenuItem value="voice">Voice</MenuItem>
          </TextField>
          <TextField select label="Provider" value={form.provider_name} onChange={set("provider_name")} fullWidth>
            <MenuItem value="telnyx">Telnyx</MenuItem>
            <MenuItem value="twilio">Twilio</MenuItem>
            <MenuItem value="plivo">Plivo</MenuItem>
            <MenuItem value="3cx">3CX</MenuItem>
          </TextField>
          {editingId && (
            <Alert severity="info">
              Leave the credential fields blank to keep the existing key. Enter new values only to replace them.
            </Alert>
          )}
          <TextField
            label="API Key"
            value={form.api_key}
            onChange={set("api_key")}
            fullWidth
            placeholder={editingId ? "•••••••• (unchanged)" : ""}
          />
          <TextField label="API Secret" type="password" value={form.api_secret} onChange={set("api_secret")} fullWidth />
          <TextField label="Messaging Profile ID (optional)" value={form.messaging_profile_id} onChange={set("messaging_profile_id")} fullWidth />
          <FormControlLabel
            control={
              <Switch
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
              />
            }
            label="Active"
          />
        </Stack>
      </SlidePanel>

      <ConfirmDialog
        open={confirmId !== null}
        title="Delete provider?"
        message="This will remove the carrier integration. This cannot be undone."
        confirmText="Delete"
        danger
        onClose={() => setConfirmId(null)}
        onConfirm={async () => { if (confirmId !== null) await deleteProvider(confirmId); setConfirmId(null); }}
      />
    </AppPage>
  );
};

export default Providers;
